import { formatDateForDisplayUTC } from '../../../utils/dateUtils';
import { formatDate, formatDateTime } from '@/utils/displayDateTime';

/**
 * Shared formatters for the Hired Equipment screens.
 *
 * These were copy-pasted into seven components, each carrying the same comment pointing at
 * `dateUtils.formatDateForDisplayUTC` — which already existed. One copy, here.
 */

/**
 * A date-only value (expiry dates, request dates, effective dates).
 *
 * These are stored at UTC midnight, so formatting them in the viewer's local zone renders
 * 2026-12-10 as "Dec 9" anywhere west of UTC — a day early on a compliance date. `timeZone:
 * 'UTC'` is what prevents that, and is the same rule `formatDateForDisplayUTC` applies.
 */
export const shortDate = (value?: string | Date | null): string => {
  // V2-073: YYYY-MM-DD from the shared formatter, which never shifts a date-only value.
  return formatDate(value) || '–';
};

/**
 * A real moment in time (audit timestamps). Deliberately NOT UTC-pinned: unlike the dates
 * above, these denote an instant and should read in the viewer's own zone.
 */
export const dateTime = (value?: string | Date | null): string => {
  // V2-073 / V2-021: YYYY-MM-DD h:mm AM in Pacific Time.
  return formatDateTime(value) || '–';
};

/**
 * Canadian dollars, always shown as "$175.00". Pinned to en-CA: with the browser's own locale an
 * en-US machine (and CI) renders CAD as "CA$175.00".
 */
export const currency = (n: number | null | undefined): string =>
  n === null || n === undefined
    ? '–'
    : n.toLocaleString('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 2,
      });

/** Re-exported so callers that want the app-wide short form don't reach past this module. */
export { formatDateForDisplayUTC };

/**
 * M2 - who entered a vendor. Every vendor created in WRAP records its creator; a blank one was
 * imported from the legacy SubcontractorMaster sheets, which never recorded it, so it says so
 * rather than showing an empty cell that reads like missing data.
 */
export const enteredByLabel = (vendor: {
  createdBy?: { name: string; lastName: string } | null;
}): string => (vendor.createdBy ? `${vendor.createdBy.name} ${vendor.createdBy.lastName}` : 'Legacy import');

/**
 * V2-053: one unit for "per" wording – "Hours" reads "hour", so the label is "Rate per hour"
 * (never "Rate per hours"). Units that are not a plain plural are kept as written.
 */
export const perUnit = (unit?: string | null): string => {
  const value = (unit ?? '').trim();
  if (!value) return '';
  const lower = value.toLowerCase();
  if (/^[a-z]+s$/.test(lower) && !lower.endsWith('ss')) return lower.slice(0, -1);
  return lower;
};
